import { usePageTitle } from '@/hooks/usePageTitle';
import { useEffect, useState, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertTriangle, FileText, Plus, Minus, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/toast';
import api from '@/lib/api';
import type { Quote } from '@/types';
import { BlockType } from '@/types';
import BlockRenderer from '@/components/BlockRenderer';
import MultiPageBlock from '@/components/MultiPageBlock';
import { QuoteClientLogoProvider } from '@/store/quoteClientLogoContext';
import { isAutoHeightBlock, isRenderableBlock } from '@/lib/blockHelpers';
import SignatureCanvas from '@/components/SignatureCanvas';
import { A4_WIDTH_PX } from '@/constants/a4';
import { A4_LANDSCAPE } from '@/constants/pageFormat';
import { formatPublicDateTime, getPublicQuoteStrings, normalizePublicQuoteLocale } from '@/lib/publicQuoteLocale';
import { ErrorBoundary } from '@/components/ErrorBoundary';

const PAGE_W_PX = A4_WIDTH_PX;
/** Gelijk aan PublicQuotePage / editor (PAGE_MAX_HEIGHT_PX), niet volledige A4-papierhoogte. */
const PAGE_H_PX = A4_LANDSCAPE.contentHeight;
const PAGE_HORIZONTAL_PADDING_DESKTOP = 64;
const PAGE_HORIZONTAL_PADDING_MOBILE = 16;
const MIN_SCALE_MOBILE = 0.55;
const USER_ZOOM_MIN = 0.5;
const USER_ZOOM_MAX = 3;
const USER_ZOOM_STEP = 0.2;

function clampUserZoom(v: number) {
  return Math.min(USER_ZOOM_MAX, Math.max(USER_ZOOM_MIN, v));
}

type ScaledCountersignQuoteCardProps = {
  scale: number;
  needsAutoHeight: boolean;
  children: ReactNode;
};

function ScaledCountersignQuoteCard({ scale, needsAutoHeight, children }: ScaledCountersignQuoteCardProps) {
  const innerRef = useRef<HTMLDivElement>(null);
  const [contentH, setContentH] = useState(PAGE_H_PX);

  const measure = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    setContentH(Math.max(Math.ceil(el.offsetHeight), 1));
  }, []);

  useEffect(() => {
    if (!needsAutoHeight) {
      setContentH(PAGE_H_PX);
      return;
    }
    measure();
    const el = innerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measure());
    ro.observe(el);
    return () => ro.disconnect();
  }, [needsAutoHeight, measure, scale]);

  const outerW = PAGE_W_PX * scale;
  const outerH = (needsAutoHeight ? contentH : PAGE_H_PX) * scale;

  return (
    <div
      className="shrink-0 overflow-hidden"
      style={{ width: outerW, height: outerH, position: 'relative' }}
    >
      <div
        ref={innerRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: PAGE_W_PX,
          minHeight: needsAutoHeight ? undefined : PAGE_H_PX,
          height: needsAutoHeight ? undefined : PAGE_H_PX,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
}

export default function CountersignPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [quote, setQuote] = useState<Quote | null>(null);
  usePageTitle('Medeondertekenen', quote?.title);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  const pagesContainerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [userZoom, setUserZoom] = useState(1);
  const [isMobileViewport, setIsMobileViewport] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
  );
  const [layoutHorizontalPad, setLayoutHorizontalPad] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640
      ? PAGE_HORIZONTAL_PADDING_MOBILE
      : PAGE_HORIZONTAL_PADDING_DESKTOP
  );

  const locale = useMemo(() => normalizePublicQuoteLocale(quote?.publicLocale), [quote?.publicLocale]);
  const cs = useMemo(() => getPublicQuoteStrings(locale).countersign, [locale]);
  const effectiveScale = scale * userZoom;
  const zoomIn = useCallback(() => setUserZoom((v) => clampUserZoom(v + USER_ZOOM_STEP)), []);
  const zoomOut = useCallback(() => setUserZoom((v) => clampUserZoom(v - USER_ZOOM_STEP)), []);
  const zoomReset = useCallback(() => setUserZoom(1), []);

  useEffect(() => {
    const el = pagesContainerRef.current;
    if (!el) return;
    const compute = () => {
      const pad = el.clientWidth < 640 ? PAGE_HORIZONTAL_PADDING_MOBILE : PAGE_HORIZONTAL_PADDING_DESKTOP;
      setLayoutHorizontalPad(pad);
      const available = el.clientWidth - pad;
      const raw = Math.min(1, available / PAGE_W_PX);
      const isMobile = el.clientWidth < 640;
      setIsMobileViewport(isMobile);
      setScale(isMobile ? Math.max(raw, MIN_SCALE_MOBILE) : raw);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [quote]);

  useEffect(() => {
    const el = pagesContainerRef.current;
    if (!el) return;
    let startDist = 0;
    let startZoom = 1;
    const distance = (a: Touch, b: Touch) => {
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      return Math.hypot(dx, dy);
    };
    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 2) return;
      startDist = distance(e.touches[0], e.touches[1]);
      startZoom = userZoom;
    };
    const onMove = (e: TouchEvent) => {
      if (e.touches.length !== 2 || startDist === 0) return;
      e.preventDefault();
      const d = distance(e.touches[0], e.touches[1]);
      setUserZoom(clampUserZoom(startZoom * (d / startDist)));
    };
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) startDist = 0;
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [quote, userZoom]);

  useEffect(() => {
    const c0 = getPublicQuoteStrings('nl').countersign;
    if (!slug || !token) {
      setError(c0.invalidLink);
      setLoading(false);
      return;
    }

    api.getCountersignQuote(slug, token)
      .then((q) => {
        setQuote(q);
        const sigBlock = q.blocks.find((b) => b.type === BlockType.SIGNATURE_PAGE);
        const content = sigBlock?.content as Record<string, unknown> | undefined;
        if (content?.senderSignature) {
          setSigned(true);
        }
      })
      .catch((err) => {
        const msg = err?.response?.data?.error || c0.loadFailed;
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [slug, token]);

  const handleSubmit = useCallback(async () => {
    if (!slug || !token || !signatureData) return;
    setSubmitting(true);
    const strings = getPublicQuoteStrings(normalizePublicQuoteLocale(quote?.publicLocale)).countersign;
    try {
      const updated = await api.submitCountersignature(slug, token, signatureData);
      setQuote(updated);
      setSigned(true);
      toast.success(strings.toastSuccess);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error || strings.toastFail;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }, [slug, token, signatureData, quote?.publicLocale]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">{cs.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">{cs.cannotLoadTitle}</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!quote) return null;

  const sigBlock = quote.blocks.find((b) => b.type === BlockType.SIGNATURE_PAGE);
  if (!sigBlock) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">{cs.cannotLoadTitle}</h1>
          <p className="text-gray-600">
            Deze offerte bevat geen handtekeningblok voor medeondertekening. Vraag de afzender om de offerte te controleren.
          </p>
        </div>
      </div>
    );
  }
  const sigContent = (sigBlock?.content || {}) as Record<string, unknown>;
  const layoutConfig = (quote.layoutConfig || {}) as Record<string, unknown>;
  const senderName =
    (sigContent.senderName as string) ||
    (layoutConfig.senderName as string) ||
    'UBO';
  const clientName = (sigContent.recipientName as string) || cs.fallbackClient;

  const infoLine = cs.infoLine.replace('{sender}', senderName).replace('{client}', clientName);
  const sectionTitle = cs.sectionTitle.replace('{sender}', senderName);

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-gray-600" />
            <div>
              <h1 className="text-sm font-semibold text-gray-900">{cs.headerTitle}</h1>
              <p className="text-xs text-gray-500">{quote.title}</p>
            </div>
          </div>
          {signed && (
            <div className="flex items-center gap-2 text-green-700 bg-green-50 px-3 py-1.5 rounded-full">
              <CheckCircle className="h-4 w-4" />
              <span className="text-sm font-medium">{cs.coSignedBadge}</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {!signed && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">{infoLine}</p>
              <p className="text-sm text-blue-700 mt-1">{cs.infoSub}</p>
            </div>
          </div>
        )}

        <QuoteClientLogoProvider blocks={quote.blocks} layoutConfig={quote.layoutConfig}>
        <div
          ref={pagesContainerRef}
          className="mx-auto overflow-x-auto px-2 py-0 sm:overflow-x-visible sm:px-4"
          style={{ maxWidth: PAGE_W_PX + layoutHorizontalPad }}
        >
          <div className="space-y-6">
            {quote.blocks.filter(isRenderableBlock).map((block) => {
              if (block.type === BlockType.SIGNATURE_PAGE) return null;

              const needsAutoHeight = isAutoHeightBlock(block.type);

              return (
                <MultiPageBlock
                  key={block.id}
                  block={block}
                  splitOverflow={false}
                  renderPage={(pageBlock, pageKey) => (
                    <div key={pageKey} className="mb-6 flex w-full justify-start sm:justify-center">
                      <ScaledCountersignQuoteCard scale={effectiveScale} needsAutoHeight={needsAutoHeight}>
                        <div
                          className="rounded-xl border bg-card shadow-sm"
                          style={{
                            width: PAGE_W_PX,
                            minHeight: needsAutoHeight ? undefined : PAGE_H_PX,
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                          }}
                        >
                          <div style={{ width: '100%', display: 'flex', flex: 1, flexDirection: 'column' }}>
                            <ErrorBoundary level="block">
                              <BlockRenderer
                                block={pageBlock}
                                isEditable={false}
                                isPublic
                                publicLocale={locale}
                                variableMap={quote.variableMap}
                              />
                            </ErrorBoundary>
                          </div>
                        </div>
                      </ScaledCountersignQuoteCard>
                    </div>
                  )}
                />
              );
            })}
          </div>
        </div>
        </QuoteClientLogoProvider>

        {isMobileViewport && (
          <div
            className="fixed right-3 bottom-4 z-[110] flex flex-col items-stretch gap-0 overflow-hidden rounded-full border bg-white/95 shadow-lg backdrop-blur"
            style={{ bottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
            role="group"
            aria-label="Zoom"
          >
            <button
              type="button"
              onClick={zoomIn}
              disabled={userZoom >= USER_ZOOM_MAX - 0.001}
              className="flex h-10 w-10 items-center justify-center text-foreground transition hover:bg-muted disabled:opacity-40"
              aria-label="Inzoomen"
            >
              <Plus className="h-5 w-5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={zoomReset}
              className="flex h-9 w-10 items-center justify-center border-t border-b text-[10px] font-semibold text-muted-foreground transition hover:bg-muted"
              aria-label="Zoom terugzetten"
              title={`${Math.round(userZoom * 100)}%`}
            >
              {userZoom === 1 ? <Maximize2 className="h-4 w-4" aria-hidden /> : `${Math.round(userZoom * 100)}%`}
            </button>
            <button
              type="button"
              onClick={zoomOut}
              disabled={userZoom <= USER_ZOOM_MIN + 0.001}
              className="flex h-10 w-10 items-center justify-center text-foreground transition hover:bg-muted disabled:opacity-40"
              aria-label="Uitzoomen"
            >
              <Minus className="h-5 w-5" aria-hidden />
            </button>
          </div>
        )}

        <div className="mt-8 bg-white rounded-lg border shadow-sm p-6 max-w-xl mx-auto">
          <h2 className="text-lg font-bold text-gray-900 mb-1">{sectionTitle}</h2>
          <p className="text-sm text-gray-500 mb-4">{cs.sectionIntro}</p>

          {signed ? (
            <div className="rounded-md border-2 border-green-500 bg-green-50 p-4 text-center">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <p className="text-green-800 font-medium">{cs.doneTitle}</p>
              {typeof sigContent.senderSignedAt === 'string' && (
                <p className="text-sm text-green-600 mt-1">
                  {formatPublicDateTime(locale, sigContent.senderSignedAt as string)}
                </p>
              )}
            </div>
          ) : (
            <>
              <SignatureCanvas locale={locale} onSignatureChange={setSignatureData} />
              <Button
                size="lg"
                className="w-full mt-4 bg-green-600 hover:bg-green-700 gap-2"
                onClick={handleSubmit}
                disabled={submitting || !signatureData}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {cs.submitting}
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    {cs.submit}
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
